import type { PromptHistoryFileType } from "../../hooks";
import { type DataLoadFile, type DataLoadFileKind, fileTypeOf } from "./consts";

export type PromptSeed = {
	prompt: string;
	tabInstructions: Record<string, string>;
};

export type PromptSeeds = Record<PromptHistoryFileType, PromptSeed>;

export const EMPTY_SEED: PromptSeed = { prompt: "", tabInstructions: {} };

export const emptySeeds = (): PromptSeeds => ({ nurses: EMPTY_SEED, shifts: EMPTY_SEED, template: EMPTY_SEED });

export const seedFor = (kind: DataLoadFileKind | null, seeds: PromptSeeds): PromptSeed => (kind ? seeds[fileTypeOf(kind)] : EMPTY_SEED);

/**
 * Back-fill the seed into every file of `fileType` that the onboarder has not typed into.
 *
 * Two paths need this and they race: `useHydrateFromHistory` fires once per (department, file type),
 * so a file dropped BEFORE the query resolves is picked up here, and one dropped after is seeded on
 * add. Both only ever write to non-dirty files, so running either twice is a no-op.
 *
 * `tab_instructions` seed a single-file run only. The saved sheet map describes whichever file wrote
 * the row, so applying it to a different file's sheets is simply wrong.
 */
export function applySeedToFiles(files: DataLoadFile[], fileType: PromptHistoryFileType, seed: PromptSeed): DataLoadFile[] {
	const matching = files.filter((file) => file.kind && fileTypeOf(file.kind) === fileType);
	if (matching.length === 0) return files;
	const seedTabs = matching.length === 1;

	return files.map((file) => {
		if (!file.kind || fileTypeOf(file.kind) !== fileType || file.promptDirty) return file;
		return {
			...file,
			prompt: seed.prompt,
			tabInstructions: seedTabs ? seed.tabInstructions : file.tabInstructions,
		};
	});
}

/**
 * The prompt a file should carry after its kind changes. A file the onboarder has typed into keeps
 * their text: a mismatched prompt they can see beats one silently swapped out from under them.
 */
export function promptAfterKindChange(file: DataLoadFile, nextKind: DataLoadFileKind, seeds: PromptSeeds): string {
	if (file.promptDirty) return file.prompt;
	return seedFor(nextKind, seeds).prompt;
}
